import {
	useGetAreas,
	useGetProperties,
	useGetSectors,
	useGetSoilAnalyses,
} from '@practium/features/api';
import { useEffect, useMemo, useRef, useState } from 'react';
import { DEFAULT_CORRECTION_FORM } from '../constants/correction.constants';
import { CorrectionFormSchema } from '../schemas/correction.schema';
import {
	buildCorrectionSectorOptions,
	getCorrectionFieldsClearedByChange,
	updateCorrectionFormField,
	validateCorrectionRelationships,
} from '../services/correctionFormOptions';
import type { CorrectionFormData } from '../types';

export interface UseCorrectionFormOptions {
	initialData?: CorrectionFormData;
	onSubmit: (data: CorrectionFormData) => void | Promise<void>;
}

export const useCorrectionForm = ({ initialData, onSubmit }: UseCorrectionFormOptions) => {
	const initialForm = initialData
		? { ...DEFAULT_CORRECTION_FORM, ...initialData }
		: { ...DEFAULT_CORRECTION_FORM };
	const [form, setForm] = useState<CorrectionFormData>(initialForm);
	const initialFormRef = useRef(initialForm);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [errors, setErrors] = useState<Record<string, string>>({});
	const { data: properties = [], isLoading: isLoadingProperties } = useGetProperties({
		limit: 300,
	});
	const { data: areas = [], isLoading: isLoadingAreas } = useGetAreas({ limit: 300 });
	const { data: sectors = [], isLoading: isLoadingSectors } = useGetSectors({
		limit: 300,
		areaId: form.area || undefined,
		enabled: !!form.area,
	});
	const { data: soilAnalyses = [], isLoading: isLoadingSoilAnalyses } = useGetSoilAnalyses({
		areaId: form.area || undefined,
		limit: 300,
		enabled: !!form.area,
	});

	const propOptions = useMemo(
		() => properties.map((property) => ({ value: property._id, label: property.name })),
		[properties]
	);

	const areaOptions = useMemo(
		() =>
			form.prop
				? areas
					.filter((area) => area.propId === form.prop)
					.map((area) => ({ value: area._id ?? area.name, label: area.name }))
				: [],
		[areas, form.prop]
	);

	const soilAnalysisOptions = useMemo(
		() =>
			soilAnalyses.map((analysis) => ({
				value: analysis._id,
				label: analysis.name ?? analysis.createdAt ?? analysis._id,
			})),
		[soilAnalyses]
	);

	const sectorOptions = useMemo(
		() => buildCorrectionSectorOptions(sectors),
		[sectors]
	);

	useEffect(() => {
		if (initialData) {
			const next = { ...DEFAULT_CORRECTION_FORM, ...initialData };
			setForm(next);
			initialFormRef.current = next;
			setErrors({});
		}
	}, [initialData]);

	const set = <K extends keyof CorrectionFormData>(key: K, value: CorrectionFormData[K]) => {
		setForm((currentForm) => updateCorrectionFormField(currentForm, key, value));
		setErrors((currentErrors) => {
			const nextErrors = { ...currentErrors };
			getCorrectionFieldsClearedByChange(key).forEach((field) => {
				delete nextErrors[field];
			});
			return nextErrors;
		});
	};

	const isDirty = () => JSON.stringify(form) !== JSON.stringify(initialFormRef.current);

	const handleSubmit = async () => {
		const result = CorrectionFormSchema.safeParse(form);

		if (!result.success) {
			const fieldErrors: Record<string, string> = {};
			result.error.issues.forEach((issue) => {
				const path = String(issue.path[0] ?? '');
				if (path) fieldErrors[path] = issue.message;
			});
			setErrors(fieldErrors);
			return;
		}

		const relationshipErrors = validateCorrectionRelationships(form, {
			properties: propOptions,
			areas: areaOptions,
			soilAnalyses: soilAnalysisOptions,
			sectors: sectorOptions,
		});
		if (Object.keys(relationshipErrors).length > 0) {
			setErrors(relationshipErrors);
			return;
		}

		setErrors({});
		setIsSubmitting(true);
		try {
			await onSubmit(form);
		} finally {
			setIsSubmitting(false);
		}
	};

	return {
		form,
		errors,
		isLoading:
			isSubmitting ||
			isLoadingProperties ||
			isLoadingAreas ||
			isLoadingSectors ||
			isLoadingSoilAnalyses,
		set,
		handleSubmit,
		isDirty,
		propOptions,
		areaOptions,
		sectorOptions,
		soilAnalysisOptions,
	};
};
